import { BaseError } from "make-error";
import { ScriptureHttpError } from "@rsc/scripture-client";

const scriptureUtilErrorSymbol = Symbol("ScriptureUtilError");
const scriptureUtilReferenceRenderErrorSymbol = Symbol(
  "ScriptureUtilReferenceRenderError",
);
const scriptureUtilUserInputErrorSymbol = Symbol("ScriptureUtilUserInputError");
const scriptureUtilMissingTranslationErrorSymbol = Symbol(
  "ScriptureUtilMissingTranslationError",
);
const scriptureUtilMissingVerseErrorSymbol = Symbol(
  "ScriptureUtilMissingVerseError",
);
const scriptureUtilInvalidVidRangeErrorSymbol = Symbol(
  "ScriptureUtilInvalidVidRangeError",
);

export class ScriptureUtilError extends BaseError {
  // See https://github.com/supermacro/neverthrow/issues/451
  public readonly scriptureUtilError = scriptureUtilErrorSymbol;
}

export class ScriptureUtilReferenceRenderError extends ScriptureUtilError {
  // See https://github.com/supermacro/neverthrow/issues/451
  public readonly scriptureUtilReferenceRenderError =
    scriptureUtilReferenceRenderErrorSymbol;

  /**
   * Prefix a rendering failure with reference context while retaining the underlying explanation.
   */
  constructor(message: string) {
    super(`Failed to render verse reference: ${message}`);
  }
}

export class ScriptureUtilUserInputError extends ScriptureUtilError {
  // See https://github.com/supermacro/neverthrow/issues/451
  public readonly scriptureUtilUserInputError =
    scriptureUtilUserInputErrorSymbol;
}

export class ScriptureUtilMissingTranslationError extends ScriptureUtilError {
  // See https://github.com/supermacro/neverthrow/issues/451
  public readonly scriptureUtilMissingTranslationError =
    scriptureUtilMissingTranslationErrorSymbol;
}

export class ScriptureUtilMissingVerseError extends ScriptureUtilError {
  // See https://github.com/supermacro/neverthrow/issues/451
  public readonly scriptureUtilMissingVerseError =
    scriptureUtilMissingVerseErrorSymbol;
}

export class ScriptureUtilInvalidVidRangeError extends ScriptureUtilError {
  // See https://github.com/supermacro/neverthrow/issues/451
  public readonly scriptureUtilInvalidVidRangeError =
    scriptureUtilInvalidVidRangeErrorSymbol;
}

/**
 * Return a nonempty explanation for scripture failures, including HTTP responses
 * whose status text is empty. Never display response bodies, which may contain HTML.
 */
export function getScriptureErrorMessage(error: unknown): string {
  if (error instanceof ScriptureHttpError) {
    const detail = error.message.trim();
    return `Scripture request failed (HTTP ${error.response.status})${detail ? `: ${detail}` : "."}`;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  return "Failed to load scripture. Please try again.";
}
